// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import PostalCodeModal from "@guestsupply/App/Components/Product/PostalCodeModal";
import setPostalCodeModal from "@guestsupply/App/Handlers/XNG/PostalCodeModalHandler";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import Tooltip, { TooltipPresentationProps } from "@guestsupply/MobiusOverrides/Tooltip/Tooltip";
import { customXngageWidget } from "@guestsupply/XDL/constants";
import MyPhoneBook from "@guestsupply/XDL/Icons/MyPhoneBook";
import PostalCode from "@guestsupply/XDL/Icons/PostalCode";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { IconProps } from "@insite/mobius/Icon";
import { Icon, LazyImage, Link } from "@insite/mobius/index";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const session = getSession(state);
    return {
        isAuthenticated: session.isAuthenticated,
        isGuest: session.isGuest,
        shouldHide: ["CheckoutReviewAndSubmitPage", "CheckoutShippingPage", "OrderConfirmationPage"].includes(
            getCurrentPage(state).type,
        ),
        primarySalesPerson: session.properties.primarySalesPerson,
        isUsWebsite: getSettingsCollection(state).customSettings?.isUsWebSite,
    };
};

const mapDispatchToProps = {
    setPostalCodeModal,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface Salesperson {
    email: string;
    imagePath: string;
    name: string;
    phone1: string;
    phone2: string;
}

export interface PostalCodeFloatingCardStyles {
    container?: InjectableCss;
    postalIcon?: IconProps;
    enterPostalCode?: InjectableCss;
    tooltipStyle?: TooltipPresentationProps;
    mail?: LinkPresentationProps;
    phone?: LinkPresentationProps;
    wrapper?: InjectableCss;
    contentContainer?: InjectableCss;
    imageContainer?: InjectableCss;
    image?: LazyImageProps;
    infoContainer?: InjectableCss;
    salesRepTypography?: TypographyProps;
}

// Xng-Changes: please check PostalCodeFloatingCardExtensions.ts for styles
export const postalCodeFloatingCardStyles: PostalCodeFloatingCardStyles = {};

const styles = postalCodeFloatingCardStyles;

const PostalCodeFloatingCard = ({
    isAuthenticated,
    isGuest,
    shouldHide,
    setPostalCodeModal,
    primarySalesPerson,
    isUsWebsite,
}: Props) => {
    const [salesPersonInfo, setSalesPersonInfo] = useState<Salesperson | undefined>(undefined);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        if (primarySalesPerson?.length) {
            setSalesPersonInfo(JSON.parse(primarySalesPerson));
        }
    }, [primarySalesPerson]);

    if (
        shouldHide ||
        (isAuthenticated && !isGuest && !primarySalesPerson?.length) ||
        (!isUsWebsite && !isAuthenticated)
    ) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            {salesPersonInfo ? (
                <Tooltip
                    {...styles.tooltipStyle}
                    placement="right"
                    trigger="click"
                    text={
                        <StyledWrapper {...styles.wrapper}>
                            <Typography {...styles.salesRepTypography}>
                                {translate("Sales Rep Contact Card")}
                            </Typography>
                            <StyledWrapper {...styles.contentContainer}>
                                <StyledWrapper {...styles.imageContainer}>
                                    <LazyImage
                                        {...styles.image}
                                        src={salesPersonInfo.imagePath}
                                        altText={salesPersonInfo.name}
                                    />
                                </StyledWrapper>

                                <StyledWrapper {...styles.infoContainer}>
                                    <Typography>{salesPersonInfo.name}</Typography>
                                    {(!!salesPersonInfo?.phone1?.length || !!salesPersonInfo?.phone2?.length) && (
                                        <Link
                                            {...styles.phone}
                                            href={`tel:${salesPersonInfo.phone1 || salesPersonInfo.phone2}`}
                                        >
                                            {salesPersonInfo.phone1 || salesPersonInfo.phone2}
                                        </Link>
                                    )}
                                    {!!salesPersonInfo?.email?.length && (
                                        <Link {...styles.mail} href={`mailto:${salesPersonInfo.email}`}>
                                            {salesPersonInfo.email}
                                        </Link>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    }
                    triggerComponent={
                        <FloatingCard iconSrc={MyPhoneBook} label={siteMessage("Guest_MyPhoneBook") as string} />
                    }
                />
            ) : (
                <>
                    <FloatingCard
                        onClick={() => setIsModalOpen(true)}
                        iconSrc={PostalCode}
                        label={!isAuthenticated ? translate("Enter Postal Code") : translate("Edit Postal Code")}
                    />
                    <PostalCodeModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
                </>
            )}
        </StyledWrapper>
    );
};

const FloatingCard = ({ onClick, iconSrc, label }: { onClick?: () => void; iconSrc: any; label: string }) => {
    return (
        <StyledWrapper
            onClick={() => {
                if (onClick) {
                    onClick();
                }
            }}
        >
            <Icon src={iconSrc} aria-label={label} {...styles.postalIcon} />
            <StyledWrapper {...styles.enterPostalCode} className="enter-postal-code">
                {label}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PostalCodeFloatingCard),
    definition: {
        group: customXngageWidget as any,
        displayName: "Postal Code Floating Card",
    },
};

export default widgetModule;
